import React from 'react';
import { CTA_PREFIXES, trackCtaClick } from '../../hooks/useClickTracking';
import { ChevronDownIcon } from '../../utils/inlineIcons';
import './PrimaryNavItem.scss';

/**
 * PrimaryNavItem - L1 navigation item for MegaMenu
 * 
 * Renders either a button (for panel triggers) or a link (for direct navigation).
 * Supports two modes:
 * - Panel trigger: Opens dropdown panel with secondary navigation
 * - Direct link: Navigates directly to a URL
 * 
 * Features:
 * - Semantic HTML: <button> for panel triggers, <a> for direct links
 * - Chevron icon for panel triggers only
 * - aria-expanded for panel triggers (accessibility)
 * - Analytics support via data-cta-id attribute
 * - Pass-through of base HTML attributes
 */

export interface PrimaryNavItemProps extends React.HTMLAttributes<HTMLElement> {
  /** L1 navigation item title */
  title: string;
  
  /** Unique identifier for this nav item */
  id: string;
  
  /** Whether this item is currently selected/active */
  isSelected?: boolean;
  
  /** Click handler for panel triggers (not used for direct links) */
  onClick?: () => void;
  
  /** Whether this is a direct link (no panel) */
  isDirectLink?: boolean;
  
  /** URL for direct link navigation (required when isDirectLink=true) */
  directLinkUrl?: string;
  
  /** CTA ID for direct link analytics (optional) */
  directLinkCtaId?: string;
}

/**
 * PrimaryNavItem component
 * 
 * @example
 * // Panel trigger
 * <PrimaryNavItem
 *   title="Products"
 *   id="products"
 *   isSelected={true}
 *   onClick={() => handlePanelToggle('products')}
 * />
 * 
 * @example
 * // Direct link
 * <PrimaryNavItem
 *   title="About"
 *   id="about"
 *   isDirectLink={true}
 *   directLinkUrl="/about"
 *   directLinkCtaId="nav-about"
 * />
 */
export const PrimaryNavItem: React.FC<PrimaryNavItemProps> = ({
  title,
  id,
  isSelected = false,
  onClick,
  isDirectLink = false,
  directLinkUrl,
  directLinkCtaId,
  className = '',
  ...baseProps
}) => {
  // Build CSS classes
  const itemClasses = [
    'poly-primary-nav-item',
    isSelected ? 'poly-primary-nav-item--selected' : '',
    className
  ].filter(Boolean).join(' ');

  // Chevron icon (only for panel triggers)
  const chevronIcon = (
    <ChevronDownIcon className="poly-primary-nav-item__chevron" aria-hidden="true" />
  );

  // Render as direct link
  if (isDirectLink && directLinkUrl) {
    return (
      <li role="none">
        <a
          href={directLinkUrl}
          className={itemClasses}
          data-cta-id={directLinkCtaId}
          onClick={() => trackCtaClick(CTA_PREFIXES.nav, { ctaId: directLinkCtaId, href: directLinkUrl, label: title })}
          {...(baseProps as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          <span className="poly-primary-nav-item__title">{title}</span>
        </a>
      </li>
    );
  }

  // Render as panel trigger button
  return (
    <li role="none">
      <button
        type="button"
        className={itemClasses}
        onClick={onClick}
        aria-expanded={isSelected}
        aria-controls={isSelected ? `panel-${id}` : undefined}
        {...(baseProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        <span className="poly-primary-nav-item__title">{title}</span>
        {chevronIcon}
      </button>
    </li>
  );
};
